import React, { useEffect } from "react";
import { Button, Badge, Popover, Dropdown } from "antd";
import { useNavigate, useLocation } from "react-router";
import BellIcon from "@/assets/icons/bell2.svg?react";
import JumpToIcon from "@/assets/icons/jump_to_element.svg?react";
import CheckboxIcon from "@/assets/icons/checkbox.svg?react";
import CustomIcon from "../UI/CustomIcon";
import { useNotifications } from "@/hooks/useNotifications";
import NotificationsPanelBody from "./NotificationsPanelBody";
import Loader from "@/components/UI/Loader";
import { useScopeParams } from "@/hooks/useScopeParams";

import type { MenuProps } from "antd";
import type { Notification } from "./notifications.d";

const items: MenuProps["items"] = [
  {
    key: "1",
    label: (
      <div className="flex items-center justify-between gap-2 cursor-pointer">
        <CustomIcon
          IconComponent={
            CheckboxIcon as React.ComponentType<{ [key: string]: unknown }>
          }
          color="#064C68"
        />
        <p className="text-[#064C68]">Mark all as read</p>
      </div>
    ),
  },
];

export default function NotificationsPanel() {
  const { id, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    unreadNotifications,
    unreadCount,
    isLoading,
    error,
    markAllAsRead,
    refetch,
  } = useNotifications(id, {
    mode: "unread",
    refetchInterval: 5 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Refetch notifications on route change
  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const handleOpenNotifications = () => {
    refetch();
  };

  const header = (
    <div className="flex w-full items-center justify-between pb-1 border-b">
      <p className="text-[#556C75] text-xs tracking-[-0.12px] ml-2 font-medium">
        SYSTEM NOTIFICATIONS
      </p>
      <Dropdown
        menu={{ items, selectable: true, onClick: handleMarkAllRead }}
        trigger={["hover"]}
        placement="bottomRight"
        disabled={unreadCount === 0}
      >
        <span
          className="text-[#556C75] cursor-pointer mr-2"
          data-testid="notification-actions-menu-trigger"
        >
          •••
        </span>
      </Dropdown>
    </div>
  );

  if (error) {
    console.error("Notifications error:", error);
  }

  const typedUnreadNotifications = unreadNotifications as Notification[];
  const contentNotifications =
    typedUnreadNotifications.length > 0 ? (
      <NotificationsPanelBody
        notifications={typedUnreadNotifications}
        trialId={id}
      />
    ) : (
      <div className="h-[600px] w-[350px] p-4 flex flex-col justify-between">
        {isLoading ? (
          <Loader />
        ) : (
          <p className="text-[#556C75]">No new notifications.</p>
        )}
        <div className="flex justify-center px-4 py-0">
          <Button
            type="default"
            className="text-[#2B4752] mt-4 px-5 py-4 h-[40px] gap-[10px] flex items-center justify-center border border-[#2B4752]"
            size="small"
            icon={<JumpToIcon />}
            onClick={() => navigate(buildUrl("notifications"))}
            data-testid="view-all-notifications-button"
          >
            <p className="text-sm">View all notifications</p>
          </Button>
        </div>
      </div>
    );

  return (
    <div>
      <Popover
        title={header}
        content={contentNotifications}
        placement="leftTop"
        arrow={false}
        autoAdjustOverflow
        overlayInnerStyle={{ paddingLeft: 0, paddingRight: 0 }}
        onOpenChange={(open) => {
          if (open) {
            handleOpenNotifications();
          }
        }}
      >
        <Button className="px-2 py-2" data-testid="notification-bell-button">
          {unreadCount > 0 ? (
            <Badge
              dot
              color="#0C98D0"
              size="medium"
              offset={[-3, 5]}
              className="notification-badge"
              data-testid="notification-bell-unread-badge"
            >
              <BellIcon className="text-lg text-[#00000073]" />
            </Badge>
          ) : (
            <BellIcon className="text-lg text-[#00000073]" />
          )}
        </Button>
      </Popover>
    </div>
  );
}
